import { Sparkles, Heart, Star, Gem } from "lucide-react";
import { BRAND } from "@/lib/brand";

const PILLARS = [
  {
    icon: Heart,
    title: "Mentor",
    body: "Pairing young queens with Crown Jewels sisters for guidance on self-worth, confidence, and community.",
  },
  {
    icon: Star,
    title: "Uplift",
    body: "Workshops, etiquette sessions, literacy events and pageantry that crown each girl in her own brilliance.",
  },
  {
    icon: Gem,
    title: "Celebrate",
    body: "Annual Miss Crown Jewels showcase — where our youngest jewels sparkle for family and sisterhood.",
  },
];

export default function MissCrownJewels() {
  return (
    <section
      id="miss"
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#080808]"
      data-testid="miss-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-5 relative" data-testid="miss-media">
          <div className="relative">
            <div className="absolute -inset-2 border border-brand/40 -translate-x-4 translate-y-4" />
            <img
              src={BRAND.images.missCrownJewels}
              alt="Miss Crown Jewels — youth mentorship sisterhood"
              className="relative w-full h-[520px] object-cover object-top"
            />
          </div>
          <div className="absolute top-6 left-6 bg-[#050505]/85 backdrop-blur-sm border border-brand/40 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">
              Ages
            </div>
            <div className="font-serif text-3xl text-brand leading-none">
              2 — 17
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-8">
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-brand mb-4 flex items-center gap-3">
              <Sparkles className="w-3.5 h-3.5 text-brand" />
              <span className="h-px w-10 bg-brand" /> Youth Mentorship
            </div>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.02]"
              data-testid="miss-title"
            >
              Miss
              <br />
              <span className="font-script text-brand text-6xl sm:text-7xl lg:text-8xl leading-none">
                Crown Jewels.
              </span>
            </h2>
          </div>

          <p className="text-lg text-white/75 leading-relaxed max-w-2xl" data-testid="miss-body">
            The Crown Jewels Sisterhood extends the crown to the next
            generation. <strong className="text-white">Miss Crown Jewels</strong> is our
            youth group and mentorship program for young queens ages
            <strong className="text-brand"> 2 through 17</strong> — a space where
            confidence is taught, character is celebrated, and every girl
            learns that her worth is non-negotiable.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 pt-4" data-testid="miss-pillars">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="group relative p-6 bg-[#0a0a0a] border border-white/10 hover:border-brand/60 transition-all duration-500"
                  data-testid={`miss-pillar-${i}`}
                >
                  <Icon className="w-6 h-6 text-brand mb-4" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl text-white mb-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    {p.body}
                  </p>
                  <span className="absolute bottom-0 left-0 h-px w-0 bg-brand group-hover:w-full transition-all duration-700" />
                </div>
              );
            })}
          </div>

          <div className="pt-4 border-t border-white/10 space-y-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4 flex items-center gap-3">
                <span className="h-px w-8 bg-brand" /> The Young Queens
              </div>
              <div
                className="grid grid-cols-2 sm:grid-cols-4 gap-3"
                data-testid="miss-roster"
              >
                {BRAND.missCrownJewels.map((q, i) => (
                  <div
                    key={q.name}
                    className="group relative bg-[#0a0a0a] border border-white/10 hover:border-brand/60 transition-all duration-500 overflow-hidden"
                    data-testid={`miss-queen-${i}`}
                  >
                    <div className="aspect-[3/4] relative overflow-hidden bg-[#050505]">
                      <img
                        src={q.image}
                        alt={`Miss Crown Jewels — ${q.name}`}
                        loading="lazy"
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-[1200ms]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />
                    </div>
                    <div className="absolute bottom-0 inset-x-0 p-3 text-center">
                      <div
                        className="font-serif text-white text-sm sm:text-base leading-tight truncate"
                        title={q.name}
                        data-testid={`miss-queen-name-${i}`}
                      >
                        {q.name}
                      </div>
                    </div>
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-0 bg-brand group-hover:w-full transition-all duration-700" />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <p className="font-script text-3xl text-brand">
                Every girl deserves a crown.
              </p>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white hover:text-brand transition-colors border-b border-brand/40 hover:border-brand pb-1"
                data-testid="miss-nominate-link"
              >
                Nominate a young queen →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
